// tslint:disable
/**
 * TC API
 * TestCollab is a test management tool that helps development teams carry out testing effectively and avoid failures. # Introduction This is an API documentation for TestCollab 2.   # Authentication TestCollab supports two forms of authentication:   - An <b>API token</b> is required to make API calls. The token can be generated from user's profile; to obtain it login to your TestCollab account and go to ['My Profile Settings'](https://testcollab.io/my_profile) and switch to API Token tab. Click on \"Generate new API token\", Once the token is generated it can be sent as a query parameter for the API calls made - https://api.testcollab.io/projects?token=<generated-API-token>    - <b>Bearer authentication</b> (also called token authentication) is an HTTP authentication scheme that involves security tokens called bearer tokens. You can generate bearer auth token using [Login](#operation/login) API.     Send this token as the Authorization header while making requests to protected resources:     `Authorization: Bearer <token>`    For making API calls, we recommend using the <b>API token</b> generated from user profile page as it won't affect the on-going user session and will be available until it is revoked by the user who generated it.      # Error Handling Error responses   - `Not Found` - Error is sent when requested resource is not available.   - `Unauthorized - invalid token` - Error is sent when API token sent with the request is invalid or revoked. Error contains status code 401        - `Forbidden` - Error is sent when logged in user is not part of the company, project or his role does not allow to complete given action. Response consist of 403 status code and more information is given in error message.
 *
 * OpenAPI spec version: 2.3.1
 * Contact: support@testcollab.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import * as runtime from "../runtime";
import {
  Agent,
  AgentFromJSON,
  AgentToJSON,
  AgentPayload,
  AgentPayloadFromJSON,
  AgentPayloadToJSON,
  AgentRun,
  AgentRunFromJSON,
  AgentRunToJSON,
  AgentRunCompletePayload,
  AgentRunCompletePayloadFromJSON,
  AgentRunCompletePayloadToJSON,
  AgentRunInputs,
  AgentRunInputsFromJSON,
  AgentRunInputsToJSON,
  AgentRunLog,
  AgentRunLogFromJSON,
  AgentRunLogToJSON,
  AgentRunLogPayload,
  AgentRunLogPayloadFromJSON,
  AgentRunLogPayloadToJSON,
  AgentRunRatingPayload,
  AgentRunRatingPayloadFromJSON,
  AgentRunRatingPayloadToJSON,
  AgentType,
  AgentTypeFromJSON,
  AgentTypeToJSON,
  TCError,
  TCErrorFromJSON,
  TCErrorToJSON,
} from "../models";

export interface CompleteAgentRunRequest {
  id: number;
  agentRunCompletePayload?: AgentRunCompletePayload;
}

export interface CreateAgentRequest {
  agentPayload?: AgentPayload;
}

export interface CreateAgentRunLogsRequest {
  agentRunLogPayload?: AgentRunLogPayload;
}

export interface DeleteAgentRequest {
  id: number;
}

export interface GetAgentRequest {
  id: number;
}

export interface GetAgentRunRequest {
  id: number;
}

export interface GetAgentRunInputsRequest {
  id: number;
}

export interface GetAgentRunLogsRequest {
  agentRun: number;
  sinceSeq?: number;
  limit?: number;
}

export interface GetAgentRunsRequest {
  agent?: number;
  project?: number;
  testPlan?: number;
  limit?: number;
  start?: number;
  sort?: string;
}

export interface GetAgentTypesRequest {
  project: number;
}

export interface GetAgentsRequest {
  project: number;
  limit?: number;
  start?: number;
  sort?: string;
}

export interface RateAgentRunRequest {
  id: number;
  agentRunRatingPayload?: AgentRunRatingPayload;
}

export interface UpdateAgentRequest {
  id: number;
  agentPayload?: AgentPayload;
}

/**
 * no description
 */
export class AgentsApi extends runtime.BaseAPI {
  /**
   * Called by the agent container with its per-run token when it finishes. Ends the run and revokes the token.
   * Report the outcome of an agent run
   */
  async completeAgentRunRaw(
    requestParameters: CompleteAgentRunRequest
  ): Promise<runtime.ApiResponse<AgentRun>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        "id",
        "Required parameter requestParameters.id was null or undefined when calling completeAgentRun."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters["Content-Type"] = "application/json";

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agentruns/{id}/complete`.replace(
        `{${"id"}}`,
        encodeURIComponent(String(requestParameters.id))
      ),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AgentRunCompletePayloadToJSON(
        requestParameters.agentRunCompletePayload
      ),
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      AgentRunFromJSON(jsonValue)
    );
  }

  /**
   * Called by the agent container with its per-run token when it finishes. Ends the run and revokes the token.
   * Report the outcome of an agent run
   */
  async completeAgentRun(
    requestParameters: CompleteAgentRunRequest
  ): Promise<AgentRun> {
    const response = await this.completeAgentRunRaw(requestParameters);
    return await response.value();
  }

  /**
   * Creates the agent, provisions the custom fields declared by its type, and creates the backing agent user so it can be assigned test cases
   * Create a new agent
   */
  async createAgentRaw(
    requestParameters: CreateAgentRequest
  ): Promise<runtime.ApiResponse<Agent>> {
    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters["Content-Type"] = "application/json";

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agents`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AgentPayloadToJSON(requestParameters.agentPayload),
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      AgentFromJSON(jsonValue)
    );
  }

  /**
   * Creates the agent, provisions the custom fields declared by its type, and creates the backing agent user so it can be assigned test cases
   * Create a new agent
   */
  async createAgent(requestParameters: CreateAgentRequest): Promise<Agent> {
    const response = await this.createAgentRaw(requestParameters);
    return await response.value();
  }

  /**
   * Called by the agent container with its per-run token. Lines are redacted before they are stored.
   * Append log lines to an agent run
   */
  async createAgentRunLogsRaw(
    requestParameters: CreateAgentRunLogsRequest
  ): Promise<runtime.ApiResponse<any>> {
    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters["Content-Type"] = "application/json";

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agentrunlogs`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AgentRunLogPayloadToJSON(requestParameters.agentRunLogPayload),
    });

    return new runtime.TextApiResponse(response);
  }

  /**
   * Called by the agent container with its per-run token. Lines are redacted before they are stored.
   * Append log lines to an agent run
   */
  async createAgentRunLogs(
    requestParameters: CreateAgentRunLogsRequest
  ): Promise<any> {
    const response = await this.createAgentRunLogsRaw(requestParameters);
    return await response.value();
  }

  /**
   * Deletes the agent and cancels any run in progress. Custom fields provisioned by this agent and the data captured in them are kept.
   * Delete an agent
   */
  async deleteAgentRaw(
    requestParameters: DeleteAgentRequest
  ): Promise<runtime.ApiResponse<any>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        "id",
        "Required parameter requestParameters.id was null or undefined when calling deleteAgent."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agents/{id}`.replace(
        `{${"id"}}`,
        encodeURIComponent(String(requestParameters.id))
      ),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.TextApiResponse(response);
  }

  /**
   * Deletes the agent and cancels any run in progress. Custom fields provisioned by this agent and the data captured in them are kept.
   * Delete an agent
   */
  async deleteAgent(requestParameters: DeleteAgentRequest): Promise<any> {
    const response = await this.deleteAgentRaw(requestParameters);
    return await response.value();
  }

  /**
   * Get an agent
   */
  async getAgentRaw(
    requestParameters: GetAgentRequest
  ): Promise<runtime.ApiResponse<Agent>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        "id",
        "Required parameter requestParameters.id was null or undefined when calling getAgent."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agents/{id}`.replace(
        `{${"id"}}`,
        encodeURIComponent(String(requestParameters.id))
      ),
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      AgentFromJSON(jsonValue)
    );
  }

  /**
   * Get an agent
   */
  async getAgent(requestParameters: GetAgentRequest): Promise<Agent> {
    const response = await this.getAgentRaw(requestParameters);
    return await response.value();
  }

  /**
   * Get an agent run
   */
  async getAgentRunRaw(
    requestParameters: GetAgentRunRequest
  ): Promise<runtime.ApiResponse<AgentRun>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        "id",
        "Required parameter requestParameters.id was null or undefined when calling getAgentRun."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agentruns/{id}`.replace(
        `{${"id"}}`,
        encodeURIComponent(String(requestParameters.id))
      ),
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      AgentRunFromJSON(jsonValue)
    );
  }

  /**
   * Get an agent run
   */
  async getAgentRun(requestParameters: GetAgentRunRequest): Promise<AgentRun> {
    const response = await this.getAgentRunRaw(requestParameters);
    return await response.value();
  }

  /**
   * Called by the agent container at startup with its per-run token. Returns the assembled prompt and the run manifest.
   * Get the inputs for an agent run
   */
  async getAgentRunInputsRaw(
    requestParameters: GetAgentRunInputsRequest
  ): Promise<runtime.ApiResponse<AgentRunInputs>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        "id",
        "Required parameter requestParameters.id was null or undefined when calling getAgentRunInputs."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agentruns/{id}/inputs`.replace(
        `{${"id"}}`,
        encodeURIComponent(String(requestParameters.id))
      ),
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      AgentRunInputsFromJSON(jsonValue)
    );
  }

  /**
   * Called by the agent container at startup with its per-run token. Returns the assembled prompt and the run manifest.
   * Get the inputs for an agent run
   */
  async getAgentRunInputs(
    requestParameters: GetAgentRunInputsRequest
  ): Promise<AgentRunInputs> {
    const response = await this.getAgentRunInputsRaw(requestParameters);
    return await response.value();
  }

  /**
   * Log lines for a run, ordered by seq. Poll with since_seq to tail a running session.
   * Get logs for an agent run
   */
  async getAgentRunLogsRaw(
    requestParameters: GetAgentRunLogsRequest
  ): Promise<runtime.ApiResponse<Array<AgentRunLog>>> {
    if (
      requestParameters.agentRun === null ||
      requestParameters.agentRun === undefined
    ) {
      throw new runtime.RequiredError(
        "agentRun",
        "Required parameter requestParameters.agentRun was null or undefined when calling getAgentRunLogs."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    if (requestParameters.agentRun !== undefined) {
      queryParameters["agent_run"] = requestParameters.agentRun;
    }

    if (requestParameters.sinceSeq !== undefined) {
      queryParameters["since_seq"] = requestParameters.sinceSeq;
    }

    if (requestParameters.limit !== undefined) {
      queryParameters["_limit"] = requestParameters.limit;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agentrunlogs`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      jsonValue.map(AgentRunLogFromJSON)
    );
  }

  /**
   * Log lines for a run, ordered by seq. Poll with since_seq to tail a running session.
   * Get logs for an agent run
   */
  async getAgentRunLogs(
    requestParameters: GetAgentRunLogsRequest
  ): Promise<Array<AgentRunLog>> {
    const response = await this.getAgentRunLogsRaw(requestParameters);
    return await response.value();
  }

  /**
   * Sessions an agent has run, newest first by default
   * Get list of agent runs
   */
  async getAgentRunsRaw(
    requestParameters: GetAgentRunsRequest
  ): Promise<runtime.ApiResponse<Array<AgentRun>>> {
    const queryParameters: runtime.HTTPQuery = {};

    if (requestParameters.agent !== undefined) {
      queryParameters["agent"] = requestParameters.agent;
    }

    if (requestParameters.project !== undefined) {
      queryParameters["project"] = requestParameters.project;
    }

    if (requestParameters.testPlan !== undefined) {
      queryParameters["test_plan"] = requestParameters.testPlan;
    }

    if (requestParameters.limit !== undefined) {
      queryParameters["_limit"] = requestParameters.limit;
    }

    if (requestParameters.start !== undefined) {
      queryParameters["_start"] = requestParameters.start;
    }

    if (requestParameters.sort !== undefined) {
      queryParameters["_sort"] = requestParameters.sort;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agentruns`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      jsonValue.map(AgentRunFromJSON)
    );
  }

  /**
   * Sessions an agent has run, newest first by default
   * Get list of agent runs
   */
  async getAgentRuns(
    requestParameters: GetAgentRunsRequest
  ): Promise<Array<AgentRun>> {
    const response = await this.getAgentRunsRaw(requestParameters);
    return await response.value();
  }

  /**
   * Agent types available for this project, read from the definition files. Types restricted to other environments are not returned.
   * Get available agent types
   */
  async getAgentTypesRaw(
    requestParameters: GetAgentTypesRequest
  ): Promise<runtime.ApiResponse<Array<AgentType>>> {
    if (
      requestParameters.project === null ||
      requestParameters.project === undefined
    ) {
      throw new runtime.RequiredError(
        "project",
        "Required parameter requestParameters.project was null or undefined when calling getAgentTypes."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    if (requestParameters.project !== undefined) {
      queryParameters["project"] = requestParameters.project;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agenttypes`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      jsonValue.map(AgentTypeFromJSON)
    );
  }

  /**
   * Agent types available for this project, read from the definition files. Types restricted to other environments are not returned.
   * Get available agent types
   */
  async getAgentTypes(
    requestParameters: GetAgentTypesRequest
  ): Promise<Array<AgentType>> {
    const response = await this.getAgentTypesRaw(requestParameters);
    return await response.value();
  }

  /**
   * Get the agents configured for a project
   * Get list of agents
   */
  async getAgentsRaw(
    requestParameters: GetAgentsRequest
  ): Promise<runtime.ApiResponse<Array<Agent>>> {
    if (
      requestParameters.project === null ||
      requestParameters.project === undefined
    ) {
      throw new runtime.RequiredError(
        "project",
        "Required parameter requestParameters.project was null or undefined when calling getAgents."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    if (requestParameters.project !== undefined) {
      queryParameters["project"] = requestParameters.project;
    }

    if (requestParameters.limit !== undefined) {
      queryParameters["_limit"] = requestParameters.limit;
    }

    if (requestParameters.start !== undefined) {
      queryParameters["_start"] = requestParameters.start;
    }

    if (requestParameters.sort !== undefined) {
      queryParameters["_sort"] = requestParameters.sort;
    }

    const headerParameters: runtime.HTTPHeaders = {};

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agents`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters,
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      jsonValue.map(AgentFromJSON)
    );
  }

  /**
   * Get the agents configured for a project
   * Get list of agents
   */
  async getAgents(requestParameters: GetAgentsRequest): Promise<Array<Agent>> {
    const response = await this.getAgentsRaw(requestParameters);
    return await response.value();
  }

  /**
   * Rate a finished run from 1 to 5. Only the user who triggered the run may rate it, and a run may hold one rating, editable by that user.
   * Rate an agent run
   */
  async rateAgentRunRaw(
    requestParameters: RateAgentRunRequest
  ): Promise<runtime.ApiResponse<AgentRun>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        "id",
        "Required parameter requestParameters.id was null or undefined when calling rateAgentRun."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters["Content-Type"] = "application/json";

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agentruns/{id}/rate`.replace(
        `{${"id"}}`,
        encodeURIComponent(String(requestParameters.id))
      ),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AgentRunRatingPayloadToJSON(
        requestParameters.agentRunRatingPayload
      ),
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      AgentRunFromJSON(jsonValue)
    );
  }

  /**
   * Rate a finished run from 1 to 5. Only the user who triggered the run may rate it, and a run may hold one rating, editable by that user.
   * Rate an agent run
   */
  async rateAgentRun(
    requestParameters: RateAgentRunRequest
  ): Promise<AgentRun> {
    const response = await this.rateAgentRunRaw(requestParameters);
    return await response.value();
  }

  /**
   * The type of an existing agent cannot be changed
   * Update an agent
   */
  async updateAgentRaw(
    requestParameters: UpdateAgentRequest
  ): Promise<runtime.ApiResponse<Agent>> {
    if (requestParameters.id === null || requestParameters.id === undefined) {
      throw new runtime.RequiredError(
        "id",
        "Required parameter requestParameters.id was null or undefined when calling updateAgent."
      );
    }

    const queryParameters: runtime.HTTPQuery = {};

    const headerParameters: runtime.HTTPHeaders = {};

    headerParameters["Content-Type"] = "application/json";

    if (this.configuration && this.configuration.apiKey) {
      headerParameters["Authorization"] =
        this.configuration.apiKey("Authorization"); // bearerAuth authentication
    }

    const response = await this.request({
      path: `/agents/{id}`.replace(
        `{${"id"}}`,
        encodeURIComponent(String(requestParameters.id))
      ),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: AgentPayloadToJSON(requestParameters.agentPayload),
    });

    return new runtime.JSONApiResponse(response, (jsonValue) =>
      AgentFromJSON(jsonValue)
    );
  }

  /**
   * The type of an existing agent cannot be changed
   * Update an agent
   */
  async updateAgent(requestParameters: UpdateAgentRequest): Promise<Agent> {
    const response = await this.updateAgentRaw(requestParameters);
    return await response.value();
  }
}
